import axios from 'axios';

// Get API base URL from environment variable
// Must be set in .env file as REACT_APP_API_URL
// Example: REACT_APP_API_URL=http://localhost:5001/api
//          REACT_APP_API_URL=https://your-backend-domain.com/api
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  
  if (!apiUrl) {
    throw new Error('REACT_APP_API_URL is not defined in environment variables. Please set it in your .env file.');
  }
  
  // Ensure the URL ends with /api (unless it's already there)
  if (!apiUrl.endsWith('/api')) {
    // Remove trailing slash if present, then add /api
    return apiUrl.replace(/\/$/, '') + '/api';
  }
  
  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();

// Log the API base URL in development only
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_BASE_URL);
  console.log('Environment:', process.env.NODE_ENV);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 404) {
        console.error('API Route not found:', {
          url: error.config?.url,
          method: error.config?.method,
          fullUrl: `${error.config?.baseURL}${error.config?.url}`,
          error: error.response.data
        });
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('API Request failed - No response:', {
        url: error.config?.url,
        method: error.config?.method,
        fullUrl: `${error.config?.baseURL}${error.config?.url}`
      });
    }
    return Promise.reject(error);
  }
);

// Topics API
export const getTopics = () => api.get('/topics');
export const getTopic = (id) => api.get(`/topics/${id}`);
export const createTopic = (data) => api.post('/topics', data);
export const updateTopic = (id, data) => api.put(`/topics/${id}`, data);
export const deleteTopic = (id) => api.delete(`/topics/${id}`);

// Subtopics API
export const getSubtopics = (topicId) => api.get(`/subtopics/topic/${topicId}`);
export const getSubtopic = (id) => api.get(`/subtopics/${id}`);
export const createSubtopic = (data) => api.post('/subtopics', data);
export const updateSubtopic = (id, data) => api.put(`/subtopics/${id}`, data);
export const deleteSubtopic = (id) => api.delete(`/subtopics/${id}`);

// Problems API
export const getProblems = (params) => api.get('/problems', { params });
export const getProblem = (id) => api.get(`/problems/${id}`);
export const createProblem = (data) => api.post('/problems', data);
export const updateProblem = (id, data) => api.put(`/problems/${id}`, data);
export const deleteProblem = (id) => api.delete(`/problems/${id}`);
export const toggleSolved = (id) => api.patch(`/problems/${id}/toggle-solved`);
export const toggleRevision = (id) => api.patch(`/problems/${id}/toggle-revision`);
export const getStats = () => api.get('/problems/stats/overview');

// Auth API
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (email, password, name) => api.post('/auth/register', { email, password, name });
export const getCurrentUser = () => api.get('/auth/me');
export const verifyToken = () => api.get('/auth/verify');
export const updateProfileLinks = (gfg_profile, leetcode_profile) => api.put('/auth/profile', { gfg_profile, leetcode_profile });
export const getLeetCodeStats = () => api.get('/auth/leetcode-stats');

// Users API (admin only)
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUserRole = (id, role) => api.put(`/users/${id}/role`, { role });
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const getAllUserStats = () => api.get('/users/stats/all');
export const getUserActivity = (id) => api.get(`/users/${id}/activity`);
export const getUserLeetCodeStats = (id) => api.get(`/users/${id}/leetcode-stats`);

export default api;

